/* @jsx h */
import {
  defineComponent,
  h,
  ref,
  computed,
  watch,
  onBeforeUnmount,
  onMounted,
} from "vue";
import {
  Cart,
  RestaurantCustomer,
  RestaurantCustomerAddress as CustomerAddress,
  CreateCustomerAddressData,
  CreatePhoneInOrderPayload,
} from "@lemmonhq/types";
import { RightSheet, useLocalization } from "@lemmonhq/uikit";
import { api } from "@app/data/api";
import { useShop } from "@app/vue/providers/ShopConnection";
import { useToast } from "@app/vue/providers/Toast";
import { getDeliveryCities } from "@lemmonhq/orders";
import { CustomerStep } from "./CustomerStep";
import { AddressStep } from "./AddressStep";
import { SchedulingStep } from "./SchedulingStep";
import { useAuth } from "@app/vue/providers/Auth";
import { useCart } from "@lemmonhq/orders";

interface PhoneinOrderProps {
  cart: Cart;
  onClose: (succes?: boolean) => void;
}

const initialCustomerForm = () => ({
  name: "",
  email: "",
  phone: "",
});

const initialAddressForm = (): CreateCustomerAddressData => ({
  first_name: "",
  last_name: "",
  street: "",
  postal_code: "",
  city: "",
  email: "",
  phone: "",
  company_name: "",
  company_registration: "",
  notes: "",
  latitude: undefined,
  longitude: undefined,
});

export const PhoneinOrder = defineComponent<PhoneinOrderProps>({
  name: "PhoneinOrder",
  props: ["cart", "onClose"],
  setup(props) {
    const shop = useShop();
    const auth = useAuth();
    const toast = useToast();
    const cart = useCart();
    const { t } = useLocalization();
    const orderType = ref<"delivery" | "takeaway">("delivery");
    const selectedCustomer = ref<RestaurantCustomer | null>(null);
    const customerAddresses = ref<CustomerAddress[]>([]);
    const selectedAddressId = ref<string | undefined>(undefined);
    const showAddressForm = ref(false);
    const isLoadingAddresses = ref(false);

    const customerSearch = ref("");
    const isSearching = ref(false);
    const searchResults = ref<RestaurantCustomer[]>([]);
    const searchError = ref("");

    const showNewCustomerForm = ref(false);
    const newCustomerForm = ref(initialCustomerForm());
    const newCustomerError = ref("");
    const isCreatingCustomer = ref(false);

    const addressForm = ref<CreateCustomerAddressData>(initialAddressForm());
    const addressFormError = ref("");
    const isCreatingAddress = ref(false);

    const selectedTime = ref<string>("");
    const orderNotes = ref("");
    const isSubmitting = ref(false);
    const submissionError = ref("");

    const manualDeliveryFee = ref<string>("");
    const showManualDeliveryFeeInput = ref(false);

    const requiresAddress = computed(() => orderType.value === "delivery");

    const cartTotal = computed(() =>
      props.cart.items.reduce((sum, item) => {
        const base = Number.parseFloat(item.price) || 0;
        const bundle = Number.parseFloat(item.bundlePrice || "") || 0;
        return sum + (base + bundle) * item.quantity;
      }, 0),
    );

    const deliveryFeeInfo = computed(() => {
      if (orderType.value !== "delivery" || !selectedAddressId.value) {
        return null;
      }

      const selectedAddress = customerAddresses.value.find(
        (addr) => addr.id === selectedAddressId.value,
      );

      if (!selectedAddress) {
        return null;
      }

      const deliveryCities = getDeliveryCities(
        auth.user.value?.restaurant_settings || [],
      );
      const matchingCity = deliveryCities.find(
        (city) =>
          city.name.toLowerCase() === selectedAddress.city.toLowerCase(),
      );

      if (matchingCity) {
        return {
          city: matchingCity.name,
          fee: parseFloat(matchingCity.fee),
          isServed: true,
        };
      }

      // City is not served - allow manual entry
      return {
        city: selectedAddress.city,
        fee: manualDeliveryFee.value ? parseFloat(manualDeliveryFee.value) : 0,
        isServed: false,
      };
    });

    const deliveryFee = computed(() => {
      return deliveryFeeInfo.value?.fee || 0;
    });

    const totalWithDelivery = computed(() => {
      return cartTotal.value + deliveryFee.value;
    });

    const timeSlots = computed(() => {
      const slots: Array<{ value: string; label: string }> = [];
      const now = new Date();
      const start = new Date(now.getTime() + 20 * 60 * 1000);

      for (let i = 0; i < 16; i++) {
        const time = new Date(start.getTime() + i * 15 * 60 * 1000);
        slots.push({
          value: time.toISOString(),
          label: time.toLocaleTimeString("en-US", {
            hour: "2-digit",
            minute: "2-digit",
            hour12: false,
          }),
        });
      }
      return slots;
    });

    watch(
      () => timeSlots.value,
      (slots) => {
        if (!selectedTime.value && slots.length) {
          selectedTime.value = slots[0].value;
        }
      },
      { immediate: true },
    );

    let searchDebounce: ReturnType<typeof setTimeout> | null = null;
    let searchToken = 0;
    const skipSearchSignature = ref<string | null>(null);

    const resetAddressForm = () => {
      addressForm.value = initialAddressForm();
      addressFormError.value = "";
    };

    const hydrateAddressFormFromCustomer = (customer: RestaurantCustomer) => {
      const fullName = customer.name || "";
      const [firstName, ...rest] = fullName.split(" ");
      const lastName = rest.join(" ");

      addressForm.value.first_name = firstName || "";
      addressForm.value.last_name = lastName || "";
      addressForm.value.email = customer.email || "";
    };

    const normalizeAddressPayload = (
      payload: CreateCustomerAddressData,
    ): CreateCustomerAddressData => {
      const base: CreateCustomerAddressData = {
        first_name: payload.first_name.trim(),
        last_name: payload.last_name.trim(),
        street: payload.street.trim(),
        postal_code: payload.postal_code.trim(),
        city: payload.city.trim(),
        email: payload.email.trim(),
        phone: payload.phone.trim(),
      };

      if (payload.company_name?.trim()) {
        base.company_name = payload.company_name.trim();
      }
      if (payload.company_registration?.trim()) {
        base.company_registration = payload.company_registration.trim();
      }
      if (payload.notes?.trim()) {
        base.notes = payload.notes.trim();
      }

      if (payload.latitude) {
        base.latitude = payload.latitude;
      }
      if (payload.longitude) {
        base.longitude = payload.longitude;
      }

      return base;
    };

    const loadCustomerAddresses = async (customerId: string) => {
      isLoadingAddresses.value = true;
      addressFormError.value = "";
      try {
        const addresses = await api.getCustomerAddresses(customerId);
        customerAddresses.value = addresses;
        selectedAddressId.value = addresses[0]?.id ?? "";
        showAddressForm.value = false;
      } catch (error) {
        console.error("Failed to load customer addresses:", error);
        addressFormError.value =
          error instanceof Error ? error.message : String(error);
        customerAddresses.value = [];
        selectedAddressId.value = "";
      } finally {
        isLoadingAddresses.value = false;
      }
    };

    const selectCustomer = async (customer: RestaurantCustomer) => {
      selectedCustomer.value = customer;
      const nextSignature = (customer.name || customer.email || "")
        .trim()
        .toLowerCase();
      skipSearchSignature.value = nextSignature || null;
      if (searchDebounce) {
        clearTimeout(searchDebounce);
        searchDebounce = null;
      }
      customerSearch.value = customer.name || customer.email || "";
      searchResults.value = [];
      isSearching.value = false;
      showNewCustomerForm.value = false;
      newCustomerError.value = "";
      resetAddressForm();
      hydrateAddressFormFromCustomer(customer);
      await loadCustomerAddresses(customer.id);
    };

    const triggerSearch = (query: string) => {
      if (searchDebounce) {
        clearTimeout(searchDebounce);
      }

      if (!query || query.trim().length < 2) {
        searchResults.value = [];
        searchError.value = "";
        return;
      }

      const normalizedQuery = query.trim().toLowerCase();
      if (
        skipSearchSignature.value &&
        normalizedQuery === skipSearchSignature.value
      ) {
        skipSearchSignature.value = null;
        searchResults.value = [];
        isSearching.value = false;
        return;
      }

      searchDebounce = setTimeout(async () => {
        const token = ++searchToken;
        isSearching.value = true;
        searchError.value = "";

        try {
          const response = await api.getCustomers({
            search: query.trim(),
            limit: 8,
          });

          if (token === searchToken) {
            searchResults.value = response.data;
          }
        } catch (error) {
          console.error("Failed to search customers:", error);
          if (token === searchToken) {
            searchError.value =
              error instanceof Error ? error.message : String(error);
            searchResults.value = [];
          }
        } finally {
          if (token === searchToken) {
            isSearching.value = false;
          }
        }
      }, 250);
    };

    const handleCreateCustomer = async () => {
      if (
        !newCustomerForm.value.name.trim() ||
        !newCustomerForm.value.email.trim()
      ) {
        newCustomerError.value = "Name and email are required.";
        return;
      }

      newCustomerError.value = "";
      isCreatingCustomer.value = true;

      try {
        const created = await api.createCustomer({
          name: newCustomerForm.value.name.trim(),
          email: newCustomerForm.value.email.trim(),
        });

        await selectCustomer(created);

        newCustomerForm.value = initialCustomerForm();
        if (requiresAddress.value) {
          showAddressForm.value = true;
        }
      } catch (error) {
        console.error("Failed to create customer:", error);
        newCustomerError.value =
          error instanceof Error ? error.message : String(error);
      } finally {
        isCreatingCustomer.value = false;
      }
    };

    const handleCreateAddress = async () => {
      if (!selectedCustomer.value) {
        addressFormError.value = "Please select a customer first.";
        return;
      }

      const requiredKeys: Array<keyof CreateCustomerAddressData> = [
        "first_name",
        "last_name",
        "street",
        "postal_code",
        "city",
        "email",
        "phone",
      ];

      const missing = requiredKeys.filter(
        (key) => !addressForm.value[key]?.trim(),
      );

      if (missing.length) {
        const fieldLabels: Record<keyof CreateCustomerAddressData, string> = {
          first_name: "first name",
          last_name: "last name",
          street: "street",
          postal_code: "postal code",
          city: "city",
          email: "email",
          phone: "phone",
          company_name: "company name",
          company_registration: "company registration",
          notes: "notes",
          latitude: "latitude",
          longitude: "longitude",
        };

        const missingLabels = missing.map((key) => fieldLabels[key]);
        const missingText = missingLabels.join(", ");
        addressFormError.value = `Missing required fields: ${missingText}`;
        return;
      }

      addressFormError.value = "";
      isCreatingAddress.value = true;

      try {
        const created = await api.createCustomerAddress(
          selectedCustomer.value.id,
          normalizeAddressPayload(addressForm.value),
        );

        customerAddresses.value = [created, ...customerAddresses.value];
        selectedAddressId.value = created.id;
        showAddressForm.value = false;
        resetAddressForm();
      } catch (error) {
        console.error("Failed to create customer address:", error);
        addressFormError.value =
          error instanceof Error ? error.message : String(error);
      } finally {
        isCreatingAddress.value = false;
      }
    };

    const isFormValid = computed(() => {
      if (!selectedCustomer.value) return false;
      if (!selectedTime.value) return false;
      if (requiresAddress.value && !selectedAddressId.value) return false;
      if (props.cart.items.length === 0) return false;
      return true;
    });

    watch(
      () => customerSearch.value,
      (value) => triggerSearch(value),
    );

    watch(
      () => selectedCustomer.value,
      (customer) => {
        if (!customer) {
          customerAddresses.value = [];
          selectedAddressId.value = "";
          resetAddressForm();
          showAddressForm.value = false;
        }
      },
    );

    watch(
      () => deliveryFeeInfo.value,
      (info) => {
        if (info && !info.isServed) {
          showManualDeliveryFeeInput.value = true;
        } else {
          showManualDeliveryFeeInput.value = false;
          manualDeliveryFee.value = "";
        }
      },
    );

    watch(
      () => orderType.value,
      () => {
        manualDeliveryFee.value = "";
        showManualDeliveryFeeInput.value = false;
      },
    );

    onBeforeUnmount(() => {
      if (searchDebounce) {
        clearTimeout(searchDebounce);
      }
    });

    const handlePlaceOrder = async () => {
      if (!selectedCustomer.value) return;
      if (isSubmitting.value) return;

      submissionError.value = "";
      isSubmitting.value = true;

      try {
        const trimmedNotes = orderNotes.value.trim();

        // Create a copy of the cart with delivery fee
        const cartWithDeliveryFee = {
          ...props.cart,
          deliveryFee:
            deliveryFee.value > 0
              ? {
                  label: deliveryFeeInfo.value?.isServed
                    ? `(${deliveryFeeInfo.value.city})`
                    : "(Manual)",
                  value: deliveryFee.value,
                }
              : undefined,
        };

        const payload: CreatePhoneInOrderPayload = {
          cart: cartWithDeliveryFee,
          serviceMethod: orderType.value,
          customer: {
            id: selectedCustomer.value.id,
            customerId: selectedCustomer.value.id,
            name: selectedCustomer.value.name,
            email: selectedCustomer.value.email,
            phone: selectedCustomer.value.phone,
          },
          addressId:
            requiresAddress.value && selectedAddressId.value
              ? selectedAddressId.value
              : undefined,
          scheduledFor: selectedTime.value,
          notes: trimmedNotes ? trimmedNotes : undefined,
        };

        const response = await api.createPhoneInOrder(shop, payload);
        await api.processOrderCreation(shop, response);
        toast.showToast("Phone-in order placed", {
          type: "success",
          autoHide: 5000,
        });
        props.onClose(true);
      } catch (error) {
        console.error("Failed to create phone-in order:", error);
        const message = error instanceof Error ? error.message : String(error);
        submissionError.value = message;
        toast.showToast(message, { type: "error" });
      } finally {
        isSubmitting.value = false;
      }
    };

    return () => (
      <RightSheet
        isOpen={true}
        onClose={props.onClose}
        width="980px"
        title={
          <div class="flex items-center gap-1 flex-1 pr-4">
            <div class="flex-1 flex items-center gap-3">
              <div class="rounded-full bg-primary/10 text-primary p-3">
                <span class="material-symbols-outlined text-2xl">call</span>
              </div>
              <div>
                <h2 class="text-xl font-semibold text-accent">
                  {t("pos.phonein.title")}
                </h2>
                <p class="text-base-content/70 text-sm">
                  {t("pos.phonein.description")}
                </p>
              </div>
            </div>
            <div class="flex justify-end gap-2">
              <button
                class={`btn h-auto justify-start gap-2 px-5 py-2 transition-all ${
                  orderType.value === "delivery"
                    ? "btn-primary shadow-md shadow-primary/20"
                    : "btn-outline border-base-300 bg-base-100 hover:border-primary/60"
                }`}
                onClick={() => {
                  orderType.value = "delivery";
                }}
              >
                <span class="material-symbols-outlined text-4xl">
                  local_shipping
                </span>
                <span class="text-left flex-1">
                  <span class="block text-sm font-semibold">
                    {t("general.words.delivery")}
                  </span>
                </span>
              </button>
              <button
                class={`btn h-auto justify-start gap-2  px-5 py-2 transition-all ${
                  orderType.value === "takeaway"
                    ? "btn-primary shadow-md shadow-primary/20"
                    : "btn-outline border-base-300 bg-base-100 hover:border-primary/60"
                }`}
                onClick={() => {
                  orderType.value = "takeaway";
                  showAddressForm.value = false;
                }}
              >
                <span class="material-symbols-outlined text-4xl">
                  lunch_dining
                </span>
                <span class="text-left flex-1">
                  <span class="block text-sm font-semibold">
                    {t("general.words.takeaway")}
                  </span>
                </span>
              </button>
            </div>
          </div>
        }
      >
        <div class="flex flex-col h-full">
          <div class="flex-1 overflow-y-auto">
            <div class="p-6 xl:p-8 space-y-6">
              <div class="grid gap-6">
                <div class="space-y-6">
                  <CustomerStep
                    customerSearch={customerSearch.value}
                    onCustomerSearch={(value) => {
                      customerSearch.value = value;
                    }}
                    isSearching={isSearching.value}
                    searchResults={searchResults.value}
                    searchError={searchError.value}
                    onSelectCustomer={selectCustomer}
                    selectedCustomer={selectedCustomer.value}
                    onClearSelectedCustomer={() => {
                      selectedCustomer.value = null;
                      customerSearch.value = "";
                      skipSearchSignature.value = null;
                    }}
                    showNewCustomerForm={showNewCustomerForm.value}
                    onToggleNewCustomerForm={(show) => {
                      showNewCustomerForm.value = show;
                      newCustomerError.value = "";
                      if (!show) {
                        newCustomerForm.value = initialCustomerForm();
                      } else if (
                        customerSearch.value &&
                        !newCustomerForm.value.name
                      ) {
                        newCustomerForm.value.name = customerSearch.value;
                      }
                    }}
                    newCustomerForm={newCustomerForm.value}
                    onUpdateNewCustomerField={(field, value) => {
                      newCustomerForm.value = {
                        ...newCustomerForm.value,
                        [field]: value,
                      };
                    }}
                    newCustomerError={newCustomerError.value}
                    isCreatingCustomer={isCreatingCustomer.value}
                    onCreateCustomer={handleCreateCustomer}
                  />

                  <AddressStep
                    orderType={orderType.value}
                    requiresAddress={requiresAddress.value}
                    selectedCustomer={selectedCustomer.value}
                    addresses={customerAddresses.value}
                    selectedAddressId={selectedAddressId.value}
                    onSelectAddress={(id) => {
                      selectedAddressId.value = id;
                    }}
                    onRequestNewAddress={() => {
                      selectedAddressId.value = undefined;
                      showAddressForm.value = true;
                      addressFormError.value = "";
                      if (selectedCustomer.value) {
                        hydrateAddressFormFromCustomer(selectedCustomer.value);
                      }
                    }}
                    isLoadingAddresses={isLoadingAddresses.value}
                    showAddressForm={showAddressForm.value}
                    addressForm={addressForm.value}
                    onUpdateAddressForm={(field, value) => {
                      addressForm.value = {
                        ...addressForm.value,
                        [field]: value,
                      };
                    }}
                    addressFormError={addressFormError.value}
                    isCreatingAddress={isCreatingAddress.value}
                    onCreateAddress={handleCreateAddress}
                    onCancelAddress={() => {
                      showAddressForm.value = false;
                      resetAddressForm();
                    }}
                  />

                  {/* Manual Delivery Fee Section */}
                  {showManualDeliveryFeeInput.value &&
                    deliveryFeeInfo.value && (
                      <section class="rounded-3xl border border-amber-300 bg-gradient-to-r from-amber-50 via-amber-50 to-amber-100 p-6 shadow-sm">
                        <div class="flex items-start gap-4">
                          <div class="w-12 h-12 bg-amber-200 rounded-full flex items-center justify-center flex-shrink-0">
                            <span class="material-symbols-outlined text-amber-700 text-2xl">
                              local_shipping
                            </span>
                          </div>
                          <div class="flex-1 space-y-4">
                            <div>
                              <h3 class="text-lg font-semibold text-amber-900 mb-1">
                                {t("pos.phonein.city_not_in_zone")}
                              </h3>
                              <p class="text-sm text-amber-700">
                                <strong>{deliveryFeeInfo.value.city}</strong> —{" "}
                                {t("pos.phonein.not_in_zone_message")}
                              </p>
                            </div>
                            <div class="max-w-xs">
                              <label class="label">
                                <span class="label-text font-semibold text-amber-900">
                                  {t("pos.phonein.delivery_fee_chf")}
                                </span>
                              </label>
                              <input
                                type="number"
                                step="0.01"
                                min="0"
                                class="input input-bordered w-full bg-white border-amber-300 focus:border-amber-500 focus:ring-amber-500"
                                placeholder="0.00"
                                value={manualDeliveryFee.value}
                                onInput={(e) => {
                                  manualDeliveryFee.value = (
                                    e.target as HTMLInputElement
                                  ).value;
                                }}
                              />
                            </div>
                          </div>
                        </div>
                      </section>
                    )}

                  <SchedulingStep
                    orderType={orderType.value}
                    timeSlots={timeSlots.value}
                    selectedTime={selectedTime.value}
                    onSelectTime={(value) => {
                      selectedTime.value = value;
                    }}
                    orderNotes={orderNotes.value}
                    onUpdateNotes={(value) => {
                      orderNotes.value = value;
                    }}
                  />
                </div>

                <aside class="space-y-6 self-start xl:sticky xl:top-6">
                  <div class="rounded-3xl border bg-base-100/90 p-6 shadow-lg space-y-5">
                    <div class="flex items-center justify-between gap-3">
                      <h3 class="text-lg font-semibold flex items-center gap-2 text-accent">
                        <span class="material-symbols-outlined text-primary">
                          receipt_long
                        </span>
                        {t("general.words.cart")}
                      </h3>
                      <span class="badge badge-outline">
                        {props.cart.items.length}{" "}
                        {props.cart.items.length === 1
                          ? t("general.words.item")
                          : t("general.words.items")}
                      </span>
                    </div>

                    <div class="rounded-2xl bg-base-200/60 p-4 space-y-3 max-h-[320px] overflow-y-auto pr-1">
                      {props.cart.items.map((item, index) => {
                        const base = Number.parseFloat(item.price) || 0;
                        const bundle =
                          Number.parseFloat(item.bundlePrice || "") || 0;
                        const lineTotal = (base + bundle) * item.quantity || 0;
                        return (
                          <div
                            key={`${item.cartId}-${index}`}
                            class="flex items-start justify-between gap-3 border-b border-base-300/60 pb-3 last:border-b-0 last:pb-0"
                          >
                            <div class="space-y-1">
                              <p class="font-medium">
                                {item.quantity}× {item.name}
                              </p>
                              {item.selectedBundles &&
                                item.selectedBundles.length > 0 && (
                                  <ul class="text-xs text-base-content/60 list-disc ml-4 space-y-1">
                                    {item.selectedBundles.map((opt) => (
                                      <li key={opt.name}>{opt.name}</li>
                                    ))}
                                  </ul>
                                )}
                            </div>
                            <span class="text-sm font-semibold">
                              {auth.user.value?.restaurant.currency || "CHF"}{" "}
                              {lineTotal.toFixed(2)}
                            </span>
                          </div>
                        );
                      })}
                      {!props.cart.items.length && (
                        <div class="text-sm text-base-content/60 text-center">
                          {t("pos.phonein.cart_empty")}
                        </div>
                      )}
                    </div>

                    <div class="space-y-2 border-t border-base-300/70 pt-3">
                      <div class="flex items-center justify-between">
                        <span class="text-base-content/70 text-sm">
                          {t("general.words.subtotal")}
                        </span>
                        <span class="font-medium">
                          {auth.user.value?.restaurant.currency || "CHF"}{" "}
                          {cartTotal.value.toFixed(2)}
                        </span>
                      </div>
                      {deliveryFee.value > 0 && (
                        <div class="flex items-center justify-between">
                          <span class="text-base-content/70 text-sm">
                            {t("pos.phonein.delivery_fee")}{" "}
                            {deliveryFeeInfo.value?.isServed
                              ? `(${deliveryFeeInfo.value.city})`
                              : `(${t("general.words.manual")})`}
                          </span>
                          <span class="font-medium">
                            {auth.user.value?.restaurant.currency || "CHF"}{" "}
                            {deliveryFee.value.toFixed(2)}
                          </span>
                        </div>
                      )}
                      <div class="flex items-center justify-between border-t border-base-300/70 pt-2">
                        <span class="text-base-content font-semibold">
                          {t("general.words.total")}
                        </span>
                        <span class="text-lg font-semibold">
                          {auth.user.value?.restaurant.currency || "CHF"}{" "}
                          {totalWithDelivery.value.toFixed(2)}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div class="rounded-3xl border bg-base-100/80 p-6 shadow">
                    <h3 class="text-lg font-semibold flex items-center gap-2 mb-3 text-accent">
                      <span class="material-symbols-outlined text-accent">
                        person_pin_circle
                      </span>
                      {t("pos.phonein.order_summary")}
                    </h3>
                    <div class="space-y-4 text-sm">
                      <div class="flex items-center justify-between">
                        <span class="text-base-content/70">
                          {t("pos.phonein.order_type")}
                        </span>
                        <span class="badge badge-accent badge-outline uppercase">
                          {orderType.value}
                        </span>
                      </div>
                      <div class="flex items-center justify-between">
                        <span class="text-base-content/70">
                          {t("pos.phonein.scheduled_for")}
                        </span>
                        <span class="font-medium">
                          {selectedTime.value
                            ? new Date(selectedTime.value).toLocaleTimeString(
                                [],
                                {
                                  hour: "2-digit",
                                  minute: "2-digit",
                                },
                              )
                            : "Not set"}
                        </span>
                      </div>
                      <div>
                        <span class="text-base-content/70 block mb-1">
                          {t("general.words.customer")}
                        </span>
                        <p class="font-medium">
                          {selectedCustomer.value
                            ? selectedCustomer.value.name ||
                              selectedCustomer.value.email
                            : "Not selected"}
                        </p>
                      </div>
                      {requiresAddress.value && (
                        <div>
                          <span class="text-base-content/70 block mb-1">
                            {t("pos.phonein.deliver_to")}
                          </span>
                          {selectedAddressId.value ? (
                            (() => {
                              const address = customerAddresses.value.find(
                                (a) => a.id === selectedAddressId.value,
                              );
                              if (!address) {
                                return (
                                  <p class="text-sm text-error">
                                    {t("pos.phonein.no_address")}
                                  </p>
                                );
                              }
                              return (
                                <div class="text-sm space-y-1">
                                  <p class="font-medium">{address.name}</p>
                                  <p class="text-base-content/70 leading-snug">
                                    {address.street}
                                    <br />
                                    {address.postal_code} {address.city}
                                  </p>
                                </div>
                              );
                            })()
                          ) : (
                            <p class="text-sm text-error">
                              No address selected
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </aside>
              </div>
            </div>
          </div>

          <div class="border-t border-base-300 bg-base-100/90 px-6 py-5 backdrop-blur">
            <div class="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div class="flex items-center gap-3 text-sm text-accent">
                <span class="material-symbols-outlined ">shopping_bag</span>
                <span>
                  {props.cart.items.length}{" "}
                  {props.cart.items.length === 1
                    ? t("general.words.item")
                    : t("general.words.items")}
                  {deliveryFee.value > 0 && (
                    <>
                      {" "}
                      · {t("general.words.subtotal")}{" "}
                      <strong>
                        {auth.user.value?.restaurant.currency || "CHF"}{" "}
                        {cartTotal.value.toFixed(2)}
                      </strong>{" "}
                      +{t("general.words.delivery")}{" "}
                      <strong>
                        {auth.user.value?.restaurant.currency || "CHF"}{" "}
                        {deliveryFee.value.toFixed(2)}
                      </strong>
                    </>
                  )}
                  {deliveryFee.value === 0 && (
                    <>
                      {" "}
                      · {t("general.words.total")}{" "}
                      <strong>
                        {auth.user.value?.restaurant.currency || "CHF"}{" "}
                        {cartTotal.value.toFixed(2)}
                      </strong>
                    </>
                  )}
                </span>
              </div>
              <button
                class={`btn btn-primary btn-lg w-full gap-3 sm:w-auto sm:min-w-[16rem] ${
                  !isFormValid.value || isSubmitting.value ? "btn-disabled" : ""
                }`}
                disabled={!isFormValid.value || isSubmitting.value}
                onClick={handlePlaceOrder}
              >
                <span class="material-symbols-outlined text-2xl">
                  {isSubmitting.value
                    ? "hourglass_top"
                    : orderType.value === "delivery"
                      ? "local_shipping"
                      : "check_circle"}
                </span>
                {orderType.value === "delivery"
                  ? t("pos.phonein.place_delivery_order")
                  : t("pos.phonein.place_takeaway_order")}
                <span class="ml-auto text-sm font-semibold">
                  {auth.user.value?.restaurant.currency || "CHF"}{" "}
                  {totalWithDelivery.value.toFixed(2)}
                </span>
              </button>
            </div>
            {submissionError.value && (
              <div class="mt-3 alert alert-error">
                <span class="material-symbols-outlined">error</span>
                <span>{submissionError.value}</span>
              </div>
            )}
          </div>
        </div>
      </RightSheet>
    );
  },
});
